// SPDX-License-Identifier: MIT
import './styles/tokens.css';
import './styles/reset.css';
import './styles/components.css';
import './styles/dev-band.css';
import './styles/auth-modal.css';
import './styles/next-steps-modal.css';
import './styles/mise-en-ligne-wizard.css';
import './styles/my-projects.css';
import './styles/project-attachments.css';
import './styles/welcome.css';
import './styles/welcome-shell.css';
import './styles/chrome.css';
import './styles/exemples.css';
import './styles/hero-v2.css';
import './styles/vignette.css';
import './styles/storyboard-bd.css';
import './styles/drop-zone.css';
import './styles/freemium.css';
import './styles/tarifs.css';
import './styles/facons.css';
import './styles/download.css';
import './styles/carnet-fiche.css'; // supersedes project-attachments.css block styling
import './styles/guided-first-run.css'; // guided first-run — deposit-box/folded-discovery/gauge (.gfr scope)
import './styles/welcome-layout.css'; // desktop layout B (2-col rail + work)
import './styles/compte.css'; // /compte — Mon espace (personal space)
import './styles/ink-gauge.css'; // « l'encre du forfait » — desktop plan-usage gauge
import './styles/cloud-save.css'; // « le double au propre » — desktop cloud-save indicator
import './styles/continuity-badge.css'; // « où est le dossier » — per-project continuity state
import './styles/desktop-connect.css'; // desktop "Connecter mon Claude" overlay
import './styles/terminal-journey.css'; // /connecter — "La façon Terminal" journey
import { detectLocale, setLocale } from './i18n/detect';
import { setBundleLocale, t } from './i18n/t';
import type { Provider } from './components/picker';
import { renderDevBand } from './components/dev-band';
import { renderAuthModal } from './components/auth-modal';
import { signInWithGoogle, signInWithMagicLink, verifyEmailCode } from './auth/sign-in';
import { renderWelcomePage } from './pages/welcome';
import { renderTarifsPage } from './pages/tarifs';
import { renderFaconsPage } from './pages/facons';
import { renderDownloadPage } from './pages/download';
import { renderExemplesPage } from './pages/exemples';
import { renderConnecterPage } from './pages/connecter';
import { renderComptePage } from './pages/compte';
import { getSession, signOut, observeSession } from './auth/session';
import { handleCallback } from './auth/callback';
import { createRouter, type Routes } from './router';
import { detectPlatform } from './platform';
import { ensureClaudeLinked } from './components/desktop-connect';
import {
  isAllowedReturnTo,
  stashPendingHandoff,
  completeMcpHandoff,
  tryResumePendingHandoff,
} from './auth/mcp-handoff';
import { shouldRedirectAuthedToWelcome } from './auth/landing-redirect';

function openAuthModal(providerPref: Provider): void {
  if (document.querySelector('.auth-modal')) return; // de-dupe
  const modal = renderAuthModal({
    providerPref,
    onGoogleClick: () => signInWithGoogle(providerPref),
    onMagicLinkSubmit: (email) => signInWithMagicLink(email, providerPref),
    // v1.1.0 — OTP code fallback. Verify the 6-digit code → setSession by
    // supabase-js → observeSession in the closure below sets data-authed
    // → router.navigate('/welcome') puts the user on the welcome page.
    onOtpCodeSubmit: (email, code) => verifyEmailCode(email, code),
    onCodeVerifySuccess: () => router.navigate('/welcome', { replace: true }),
  });
  document.body.appendChild(modal);
  requestAnimationFrame(() => {
    modal.querySelector<HTMLInputElement>('[data-component="auth-email-input"]')?.focus();
  });
}

// Checkout CTAs (and any flow needing sign-in) dispatch this when the visitor
// isn't authenticated → open the sign-in modal so they can complete the purchase.
window.addEventListener('genesis:need-auth', () => openAuthModal('anthropic'));

function wireLocaleToggle(root: ParentNode): void {
  root.querySelectorAll<HTMLElement>('[data-locale]').forEach((btn) => {
    if (btn.dataset.localeBound) return;
    btn.dataset.localeBound = 'true';
    btn.addEventListener('click', () => {
      const next = btn.getAttribute('data-locale') as 'fr' | 'en';
      setLocale(next);
      // Update the html lang attribute synchronously so :lang() CSS rules
      // hide/show the right content without forcing a full route re-mount.
      // For SPA routes (/welcome, /auth/callback) we still need rerender()
      // because their content is JS-rendered with t() — only the static
      // landing route is CSS-driven.
      document.documentElement.lang = next;
      // Trigger the inline live-dropzone localizer so the simulated TEXTE/
      // TEXT tag + verbs + step labels swap immediately (otherwise they
      // wait until the next 2.4s setInterval tick).
      const w = window as unknown as { __syncDropzoneLang?: () => void };
      w.__syncDropzoneLang?.();
      if (window.location.pathname !== '/') rerender();
    });
  });
}

function appRoot(): HTMLElement {
  return document.getElementById('app')!;
}

async function mountLanding(): Promise<void> {
  const locale = detectLocale();
  setBundleLocale(locale);
  document.documentElement.lang = locale;
  // The inline live-dropzone script runs at boot BEFORE this module sets
  // html.lang from localStorage, so its first cycle() uses the inline default
  // (`<html lang="fr">`). Re-fire __syncDropzoneLang now so EN-persisted
  // visitors see EN tag/verb/steps from the first frame.
  const w = window as unknown as { __syncDropzoneLang?: () => void };
  w.__syncDropzoneLang?.();

  // MCP "Connect my Claude" SSO handoff. genesis-mcp /authorize bounces an
  // unauthenticated user here with ?return_to=<mcp /authorize URL>. Once the
  // user has a Supabase session, mint a one-time code and bounce back (see
  // auth/mcp-handoff). The strict allowlist in isAllowedReturnTo prevents an
  // open redirect / one-time-code harvest via a foreign return_to.
  const returnTo = new URLSearchParams(window.location.search).get('return_to');
  if (returnTo && isAllowedReturnTo(returnTo)) {
    stashPendingHandoff(returnTo);
    const handoffSession = await getSession();
    if (handoffSession) {
      await completeMcpHandoff(returnTo);
      return; // redirecting away — skip the rest of the landing mount
    }
    openAuthModal('anthropic'); // prompt sign-in; observeSession resumes after
  }

  // Bug 5 fix 2026-05-28 : if the user already has a Supabase session AND
  // there is no MCP handoff in play, they don't belong on the logged-out
  // landing — bounce them to /welcome. Otherwise the logo / direct nav to /
  // shows them "signed out" and any CTA forces a re-login. Helper isolated
  // for testability ; see src/auth/landing-redirect.ts.
  const sessionForRedirect = await getSession();
  if (shouldRedirectAuthedToWelcome(sessionForRedirect, returnTo)) {
    router.navigate('/welcome', { replace: true });
    return;
  }

  // Show static landing, hide SPA div
  document.body.classList.remove('app-active');

  // Wire CTA buttons once (static HTML — guard prevents duplicate listeners)
  document.querySelectorAll<HTMLElement>('[data-action="open-auth"]').forEach((btn) => {
    if (btn.dataset.authWired) return;
    btn.dataset.authWired = 'true';
    btn.addEventListener('click', () => openAuthModal('anthropic'));
  });

  // Expired auth banner
  const params = new URLSearchParams(window.location.search);
  const existing = document.querySelector('[data-component="expired-banner"]');
  if (params.get('auth') === 'expired' && !existing) {
    const banner = document.createElement('div');
    banner.className = 'expired-banner';
    banner.setAttribute('data-component', 'expired-banner');
    banner.textContent = t('welcome.expiredBanner');
    const landingRoot = document.getElementById('landing-root');
    landingRoot?.insertAdjacentElement('afterbegin', banner);
  } else if (!params.get('auth') && existing) {
    existing.remove();
  }

  wireLocaleToggle(document);
}

async function mountWelcome(): Promise<void> {
  document.body.classList.add('app-active');
  const locale = detectLocale();
  setBundleLocale(locale);
  document.documentElement.lang = locale;
  const app = appRoot();
  app.innerHTML = '';

  let session = await getSession();

  // Dev-only escape hatch: ?preview-session=<email> renders the welcome page
  // with a synthetic session so we can iterate on layout without going through
  // a full magic-link round-trip. Gated by import.meta.env.DEV — Vite tree-
  // shakes this branch out of the prod bundle entirely.
  if (!session && import.meta.env.DEV) {
    const previewEmail = new URLSearchParams(window.location.search).get('preview-session');
    if (previewEmail) {
      session = {
        access_token: 'dev', refresh_token: 'dev', token_type: 'bearer',
        expires_in: 3600, expires_at: Math.floor(Date.now() / 1000) + 3600,
        user: {
          id: 'dev-preview', email: previewEmail,
          app_metadata: {}, user_metadata: { full_name: previewEmail.split('@')[0] },
          aud: 'authenticated', created_at: new Date().toISOString(),
        },
      } as never;
    }
  }

  if (!session) {
    document.body.classList.remove('app-active');
    router.navigate('/?auth=expired', { replace: true });
    return;
  }

  const page = renderWelcomePage({
    session,
    locale,
    onSignOut: async () => {
      try { await signOut(); } catch (e) { console.warn('[auth] signOut failed:', e); }
      router.navigate('/', { replace: true });
    },
  });
  app.appendChild(page);
  // DEV STATS band visible to authed users — inserted before the page's own
  // footer (which renderWelcomePage already includes with the sign-out link).
  // The page is a DocumentFragment whose children are moved into app on
  // append, so we re-query app.lastElementChild for the live footer ref.
  const footer = app.lastElementChild;
  if (footer && footer.classList.contains('site-footer')) {
    app.insertBefore(renderDevBand(), footer);
  } else {
    app.appendChild(renderDevBand());
  }
  wireLocaleToggle(app);
}

async function mountCallback(): Promise<void> {
  document.body.classList.add('app-active');
  const locale = detectLocale();
  setBundleLocale(locale);
  document.documentElement.lang = locale;
  const app = appRoot();
  app.innerHTML = '<div class="callback-pending" data-component="callback-pending" style="padding: 48px; text-align: center; color: var(--color-text-subtle);">…</div>';
  await handleCallback((path, opts) => router.navigate(path, opts));
}

// /tarifs — public pricing page. No session needed (decorative header). The
// "back" + free CTA route to /welcome ; if the user isn't authed, /welcome
// bounces them to the landing (existing guard), which is the desired flow.
// Best-effort authed chrome for the public pages. When the visitor has a
// session, thread their identity + sign-out into the page so the shared header
// renders the ✦ idées pill + account menu consistently — not only on /welcome
// and /compte (the "menu only on welcome" gap, 2026-05-30). Anon visitor →
// empty object, header keeps its decorative signed-out state (unchanged).
async function publicAuthedChrome(): Promise<{ user?: { email: string }; onSignOut?: () => Promise<void> }> {
  const session = await getSession();
  const email = session?.user?.email;
  if (!email) return {};
  return {
    user: { email },
    onSignOut: async () => {
      try { await signOut(); } catch (e) { console.warn('[auth] signOut failed:', e); }
      router.navigate('/', { replace: true });
    },
  };
}

async function mountTarifs(): Promise<void> {
  document.body.classList.add('app-active');
  const locale = detectLocale();
  setBundleLocale(locale);
  document.documentElement.lang = locale;
  const app = appRoot();
  app.innerHTML = '';
  const chrome = await publicAuthedChrome();
  app.appendChild(renderTarifsPage({ locale, onBack: () => router.navigate('/welcome'), ...chrome }));
  wireLocaleToggle(app);
}

// /connecter — thin host for the "Connecter mon Claude" onboarding flow. No session needed.
async function mountConnecter(): Promise<void> {
  document.body.classList.add('app-active');
  const locale = detectLocale();
  setBundleLocale(locale);
  document.documentElement.lang = locale;
  const app = appRoot();
  app.innerHTML = '';
  const chrome = await publicAuthedChrome();
  app.appendChild(renderConnecterPage({ locale, ...chrome }));
  wireLocaleToggle(app);
}

// /facons — public BYOAI ladder page (Web → Terminal → Desktop). No session needed.
async function mountFacons(): Promise<void> {
  document.body.classList.add('app-active');
  const locale = detectLocale();
  setBundleLocale(locale);
  document.documentElement.lang = locale;
  const app = appRoot();
  app.innerHTML = '';
  const chrome = await publicAuthedChrome();
  app.appendChild(renderFaconsPage({ locale, ...chrome }));
  wireLocaleToggle(app);
}

// /download — public Genesis Desktop beta download. No session needed.
async function mountDownload(): Promise<void> {
  document.body.classList.add('app-active');
  const locale = detectLocale();
  setBundleLocale(locale);
  document.documentElement.lang = locale;
  const app = appRoot();
  app.innerHTML = '';
  const chrome = await publicAuthedChrome();
  app.appendChild(renderDownloadPage({ locale, ...chrome }));
  wireLocaleToggle(app);
}

// /exemples — public vitrine "15 projets qui commencent bien". No session needed.
async function mountExemples(): Promise<void> {
  document.body.classList.add('app-active');
  const locale = detectLocale();
  setBundleLocale(locale);
  document.documentElement.lang = locale;
  const app = appRoot();
  app.innerHTML = '';
  const chrome = await publicAuthedChrome();
  app.appendChild(renderExemplesPage({ locale, ...chrome }));
  wireLocaleToggle(app);
}

// /compte — "Mon espace" (personal space). Session-gated like /welcome :
// no session → bounce to the landing with auth=expired.
async function mountCompte(): Promise<void> {
  document.body.classList.add('app-active');
  const locale = detectLocale();
  setBundleLocale(locale);
  document.documentElement.lang = locale;
  const app = appRoot();
  let session = await getSession();
  // DEV-only preview hatch (tree-shaken from prod) — same as /welcome : render
  // the page with a synthetic session to iterate on layout / capture responsive.
  if (!session && import.meta.env.DEV) {
    const previewEmail = new URLSearchParams(window.location.search).get('preview-session');
    if (previewEmail) {
      session = {
        access_token: 'dev', refresh_token: 'dev', token_type: 'bearer',
        expires_in: 3600, expires_at: Math.floor(Date.now() / 1000) + 3600,
        user: {
          id: 'dev-preview', email: previewEmail, app_metadata: {},
          user_metadata: { full_name: previewEmail.split('@')[0] },
          aud: 'authenticated', created_at: new Date().toISOString(),
        },
      } as never;
    }
  }
  if (!session) {
    document.body.classList.remove('app-active');
    router.navigate('/?auth=expired', { replace: true });
    return;
  }
  // Returning from a successful Mollie checkout → show the success banner; strip the
  // param so a refresh won't re-trigger it. `done` = one-time (CP-2), `cloud` = CP-3.
  const checkoutParam = new URLSearchParams(window.location.search).get('checkout');
  const justPurchased = checkoutParam === 'done';
  const justSubscribedCloud = checkoutParam === 'cloud';
  if (justPurchased || justSubscribedCloud) window.history.replaceState(null, '', '/compte');
  app.innerHTML = '';
  app.appendChild(renderComptePage({
    session,
    locale,
    justPurchased,
    justSubscribedCloud,
    onSignOut: async () => {
      try { await signOut(); } catch (e) { console.warn('[auth] signOut failed:', e); }
      router.navigate('/', { replace: true });
    },
  }));
  wireLocaleToggle(app);
}

// Single source of truth for the route table. Both the router AND rerender()
// derive from this — see rerender() for why they must not drift.
const routes: Routes = {
  '/': mountLanding,
  '/auth/callback': mountCallback,
  '/welcome': mountWelcome,
  '/tarifs': mountTarifs,
  '/facons': mountFacons,
  '/download': mountDownload,
  '/connecter': mountConnecter,
  '/exemples': mountExemples,
  '/compte': mountCompte,
};

const router = createRouter(routes);

// Delegated SPA navigation for in-app links marked data-nav (e.g. the footer
// "Tarifs" link). Intercepts same-origin path clicks → router.navigate, so we
// don't full-reload. Plain modifier-clicks (new tab) fall through to default.
document.addEventListener('click', (e) => {
  if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
  const link = (e.target as HTMLElement)?.closest<HTMLAnchorElement>('a[data-nav]');
  if (!link) return;
  const href = link.getAttribute('href');
  if (!href || !href.startsWith('/')) return;
  e.preventDefault();
  router.navigate(href);
});

function rerender(): void {
  // Re-mount the current route without touching history — preserves query params.
  // Derives from the SAME `routes` map the router uses, so the two can never
  // drift : a route present in the router but missing here used to fall through
  // to mountLanding() and eject authed users to / on a language toggle
  // (the /compte regression, 2026-05-30). Unknown path → landing, matching the
  // router's own unknown-path fallback.
  const handler = routes[window.location.pathname] ?? mountLanding;
  void handler();
}

// React to auth state changes from any tab. Single source of truth for the
// `body[data-authed]` attribute that drives dev-band visibility (CSS rule).
// No async race possible because we only set/remove an attribute synchronously.
//   /welcome -> sign-out invalidates the route -> back to /
observeSession((session) => {
  if (session) {
    document.body.setAttribute('data-authed', 'true');
    // Resume a pending MCP "Connect my Claude" handoff once the user signs in
    // (covers the magic-link email round-trip + the OTP-code path). Redirects
    // away to the mcp /authorize URL with a one-time code if one is pending.
    void tryResumePendingHandoff(session);
  } else {
    document.body.removeAttribute('data-authed');
  }
  if (!session && window.location.pathname === '/welcome') {
    // Dev-only escape hatch: don't kick users out when previewing the welcome
    // surface with a synthetic session via ?preview-session=<email>.
    if (import.meta.env.DEV && new URLSearchParams(window.location.search).get('preview-session')) {
      return;
    }
    router.navigate('/', { replace: true });
  }
});

router.start();

// Desktop only: gate the app behind a linked Claude account. On the web this is a
// no-op (never called) — the web build runs inference server-side, not via the
// local `claude` CLI. The overlay lets the user link in one click (browser OAuth)
// instead of opening a terminal. Best-effort: if the Tauri bridge isn't there yet,
// ensureClaudeLinked swallows it and the app stays usable.
if (detectPlatform() === 'desktop') {
  void ensureClaudeLinked();
}
